import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router'

import { Icon, OlarkHelper } from '@fv/client-components'
import type { FullShipment, Quote } from '@fv/client-types'

import { LoadSummary } from '@/components/LoadSummary/LoadSummary'
import { useLoad, useQuotes } from '@/hooks/shipments'
import { useLoadRatesPage } from '@/route-hooks'
import { routes } from '@/routes'
import {
  isBookedAndDispatched,
  isProcessingQuotes,
} from '@/utils/shipmentFuncs'

import { useRole } from '../auth/hooks'
import { RatesListing } from './RatesListing'
import type { RatesListingMode } from './useRatesListing'

type Props = {
  hasError: boolean
  load?: FullShipment
  rates?: Quote[]
  isLoading: boolean
  mode?: RatesListingMode
}

const RatesContainer = ({ hasError, load, rates, isLoading, mode }: Props) => {
  const role = useRole()
  const ratesPage = useLoadRatesPage()
  const [initialLoad, setInitialLoad] = useState(load)

  useEffect(() => {
    if (load && !initialLoad) {
      setInitialLoad(load)
    }
  }, [load, initialLoad])

  if (isLoading || !load) {
    return (
      <div className="min-h-0 h-full bg-fv-gray-50 p-6">
        <Icon
          className={clsx({ 'text-fv-orange': hasError })}
          icon={hasError ? 'exclamation-triangle' : 'sync'}
        />
        <span className="ml-2">
          {hasError ? 'Shipment not found.' : 'Loading...'}
        </span>
      </div>
    )
  }

  if (
    initialLoad &&
    isBookedAndDispatched(initialLoad) &&
    mode !== 'change' &&
    mode !== 'view'
  ) {
    return <Navigate to={ratesPage(load, 'view')} replace />
  }

  if (load.status === 'canceled' && mode !== 'view') {
    return <Navigate to={routes.quote(load.workflow)} replace />
  }
  if (role === 'vendor' || load.workflow !== 'truckload') {
    return <RatesListing load={load} rates={rates} />
  } else {
    return <Navigate to={ratesPage(load, mode)} replace />
  }
}

export const RatesPage = () => {
  const { loadId, mode } = useParams<{
    loadId: string
    mode?: RatesListingMode
  }>()
  const loadQuery = useLoad(loadId, { refetchWhileQuoting: true })
  const quotesQuery = useQuotes(loadId, {
    enabled: true,
    interval: query => {
      const data = query.state.data
      // If no rates, keep polling for a minute
      if (
        isProcessingQuotes(loadQuery?.data) ||
        (!data?.length && query.state.dataUpdateCount <= 4)
      ) {
        return 15000
      }

      return false
    },
  })
  return (
    <div className="shipper-page-fill shipper-grid-rows-single-fill grid grid-cols-[minmax(20rem,.3fr)_1fr] bg-white">
      <LoadSummary load={loadQuery.data} />

      <RatesContainer
        hasError={loadQuery.isError}
        isLoading={loadQuery.isLoading || quotesQuery.isLoading}
        load={loadQuery.data}
        rates={quotesQuery.data}
        mode={mode}
      />
      <OlarkHelper loadId={loadQuery.data?.loadId} />
    </div>
  )
}
